import { TaskPriority, TaskStatus } from "@common/types/task";
import {
    DEMO_ENTITIES,
    DEMO_PEOPLE,
    loadDemoTasks,
    resolveTaskDerivedFields,
    type DemoStageId,
    type DemoTask,
} from "./demoTasks";

const STRESS_TASK_COUNT = 1000;
const STRESS_SEED = 20261007;

type Weighted<T> = ReadonlyArray<readonly [T, number]>;

// Prep is the long column (about 300 cards), the rest taper off.
const STAGE_WEIGHTS: Weighted<DemoStageId | null> = [
    [null, 8],
    ["intake", 18],
    ["prep", 30],
    ["adjustments", 20],
    ["review", 14],
    ["deliver", 10],
];

// About 10% in progress, so many animated status pills are on screen; every
// other task is the agent's, so both In progress icons show.
const STATUS_WEIGHTS: Weighted<TaskStatus> = [
    [TaskStatus.TODO, 62],
    [TaskStatus.IN_PROGRESS, 10],
    [TaskStatus.DONE, 18],
    [TaskStatus.BLOCKED, 6],
    [TaskStatus.CANCELLED, 4],
];

const PRIORITY_WEIGHTS: Weighted<TaskPriority | null> = [
    [null, 50],
    [TaskPriority.LOW, 15],
    [TaskPriority.MEDIUM, 20],
    [TaskPriority.HIGH, 10],
    [TaskPriority.URGENT, 5],
];

const ENTITY_COUNT_WEIGHTS: Weighted<number> = [
    [1, 85],
    [2, 12],
    [3, 3],
];

/** Mulberry32: a small deterministic generator, so every run is the same board. */
const createRandom = (seed: number) => {
    let state = seed;
    return () => {
        state = (state + 0x6d2b79f5) | 0;
        let value = Math.imul(state ^ (state >>> 15), 1 | state);
        value ^= value + Math.imul(value ^ (value >>> 7), 61 | value);
        return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
    };
};

const pickWeighted = <T>(random: () => number, weights: Weighted<T>) => {
    const total = weights.reduce((sum, [, weight]) => sum + weight, 0);
    let roll = random() * total;
    const picked = weights.find(([, weight]) => {
        roll -= weight;
        return roll < 0;
    });
    return (picked ?? weights[weights.length - 1])[0];
};

const pickEntityIds = (random: () => number) => {
    const ids = DEMO_ENTITIES.map((entity) => entity.id);
    const count = pickWeighted(random, ENTITY_COUNT_WEIGHTS);
    const start = Math.floor(random() * ids.length);
    return Array.from(
        { length: count },
        (_, offset) => ids[(start + offset) % ids.length],
    );
};

/**
 * About 1,000 close tasks for the playground's stress mode: the demo tasks'
 * titles and types spread over stages, assignees, entities and statuses with
 * a fixed seed.
 */
export const generateStressTasks = (todayDateOnly: string): DemoTask[] => {
    const random = createRandom(STRESS_SEED);
    const templates = loadDemoTasks(todayDateOnly);
    return Array.from({ length: STRESS_TASK_COUNT }, (_, index) => {
        const template = templates[index % templates.length];
        const entityIds = pickEntityIds(random);
        const assigneeIndex = Math.floor(random() * (DEMO_PEOPLE.length + 1));
        const dueInDays =
            random() < 0.15 ? null : Math.floor(random() * 31) - 10;
        const stageId = pickWeighted(random, STAGE_WEIGHTS);
        return {
            ...template,
            ...resolveTaskDerivedFields({
                stageId,
                entityIds,
                dueInDays,
                todayDateOnly,
            }),
            id: `stress-task-${index + 1}`,
            stageId,
            status: pickWeighted(random, STATUS_WEIGHTS),
            inProgressBy: index % 2 === 0 ? "agent" : "person",
            priority: pickWeighted(random, PRIORITY_WEIGHTS),
            assigneeId:
                assigneeIndex < DEMO_PEOPLE.length
                    ? DEMO_PEOPLE[assigneeIndex].id
                    : null,
            entityIds,
            commentCount: Math.floor(random() * 6),
        };
    });
};
